/**
 * "Upload a file" — `POST /api/studio/_upload`, the studio's one-request upload.
 *
 * Not an action and therefore not `useStudioAction`: `_upload` is a
 * `multipart/form-data` route beside `_impersonate` under the same prefix
 * (`modules/files/routes.ts`: "a non-action studio route, like `_impersonate`"), and
 * an MP4 is not a JSON body. The precedent for calling one is `ImpersonateButton` and
 * `useUploadAttachment`, both of which go through `api.fetch` — see `api/client.ts`,
 * which binds that function for exactly these two callers.
 *
 * **The file part goes on last, and it is named `file`.** `modules/files/multipart.ts`
 * requires both: "metadata must be known before storage chooses a tenant folder, and a
 * malformed trailing part then fails while `storage.write` can still remove its
 * temporary file". `FormData` preserves append order, so the order below is the wire
 * order.
 *
 * **No `Content-Type` header is set.** The browser writes it, with the boundary; a
 * hand-written `multipart/form-data` has no boundary in it and the parser refuses the
 * request before a byte of the file is read.
 *
 * The form refuses what the service would refuse rather than sending it: a
 * `deliverable` is filed under a month and nothing else is (`pathFor` `assertPeriod`),
 * and a `handover` needs a work order with a final milestone that can release it
 * (`files.assertHandoverTarget` — one with none would be held for ever with nothing
 * able to unlock it). `.docs/24`: "A control shown to somebody the API will refuse is
 * worse than no control."
 *
 * `invoice_pdf` and `report_pdf` are not offered. Both are rendered and registered by
 * their owning jobs; an uploaded one would be a second document claiming to be an
 * invoice, and the row is what an invoice is.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { z } from 'zod'

import { PeriodSchema } from '@fareast/shared'

import { useApi } from '../../../api/provider.js'
import type { ApiError } from '../../../api/hooks.js'
import { moduleKey } from '../../../api/keys.js'
import { messageOf, problemOf } from '../../../api/problem.js'
import { Button } from '../../../components/portal/Button.js'
import { Drop, Field, Hint, Input, Select, useZodForm } from '../../../components/portal/Form.js'
import { Stack } from '../../../components/portal/Layout.js'
import { Dialog } from '../../../components/portal/Overlay.js'
import { Notice } from '../../../components/portal/Status.js'
import { B, Prose, Small } from '../../../components/portal/Text.js'

/** The kinds a person uploads. The two rendered PDFs are their jobs' to register. */
const KINDS = [
  { value: 'deliverable', label: 'Deliverable — filed under a month' },
  { value: 'handover', label: 'Handover — held until the final invoice is paid' },
  { value: 'brand', label: 'Brand asset' },
  { value: 'upload', label: 'Upload — something the client sent us' },
  { value: 'other', label: 'Other' },
] as const

const UploadFormSchema = z
  .object({
    kind: z.enum(['deliverable', 'handover', 'brand', 'upload', 'other']),
    title: z.string().trim().max(255),
    /** Empty unless the kind is `deliverable`, where `pathFor` insists on one. */
    period: z.string().trim(),
    /** Empty unless the kind is `handover`, where the gate needs something to open. */
    work_order_ref: z.string().trim(),
  })
  .superRefine((values, ctx) => {
    if (values.kind === 'deliverable' && !PeriodSchema.safeParse(values.period).success) {
      ctx.addIssue({
        code: 'custom',
        path: ['period'],
        message: 'A deliverable is filed under a month. Write it as 2026-08.',
      })
    }
    if (values.kind !== 'deliverable' && values.period !== '') {
      ctx.addIssue({
        code: 'custom',
        path: ['period'],
        message: 'Only a deliverable has a month to file under.',
      })
    }
    if (values.kind === 'handover' && values.work_order_ref === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['work_order_ref'],
        message:
          'A handover belongs to a work order with a final milestone — one with none would be held for ever.',
      })
    }
  })

type UploadForm = z.infer<typeof UploadFormSchema>

export type UploadFileButtonProps = {
  readonly slug: string
  readonly onDone: () => void
}

export function UploadFileButton({ slug, onDone }: UploadFileButtonProps) {
  const [open, setOpen] = useState(false)

  return (
    <Dialog
      open={open}
      onOpenChange={setOpen}
      title="Upload a file"
      trigger={<Button size="sm">Upload a file</Button>}
    >
      {/*
        Keyed on `open`, for the reason `ActionDialog` gives: a form abandoned
        half-filled comes back with the old values and the previous run's error under
        a field.
      */}
      {open ? (
        <UploadForm
          key={String(open)}
          slug={slug}
          onDone={() => {
            setOpen(false)
            onDone()
          }}
        />
      ) : null}
    </Dialog>
  )
}

function UploadForm({ slug, onDone }: { readonly slug: string; readonly onDone: () => void }) {
  const upload = useUploadStudioFile()
  const [picked, setPicked] = useState<File | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const form = useZodForm(UploadFormSchema, {
    defaultValues: { kind: 'deliverable', title: '', period: '', work_order_ref: '' },
  })

  const kind = form.watch('kind')

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        void form.handleSubmit(async (values: UploadForm) => {
          setFailure(null)
          if (picked === null) {
            setFailure('Choose a file first.')
            return
          }
          try {
            await upload.mutateAsync({ client: slug, file: picked, values })
            onDone()
          } catch (error) {
            setFailure(messageOf(error))
          }
        })(event)
      }}
    >
      <Stack>
        <Prose>
          The bytes go straight to this client’s folder and the row is written when they land. A
          deliverable is downloadable the moment it is stored; a handover is held until the final
          invoice on its work order is paid.
        </Prose>

        <Select
          control={form.control}
          name="kind"
          label="What kind of file"
          options={KINDS.map((one) => ({ value: one.value, label: one.label }))}
        />

        <Input
          control={form.control}
          name="title"
          label="Title"
          placeholder="Reels — 14 edits, 9:16"
          hint="The line the files page prints in bold. Left empty, the filename stands in."
        />

        {kind === 'deliverable' ? (
          <Input
            control={form.control}
            name="period"
            label="Month"
            placeholder="2026-08"
            hint="A deliverable is filed under a month, and nothing else is."
          />
        ) : null}

        {kind === 'handover' ? (
          <Input
            control={form.control}
            name="work_order_ref"
            label="Work order"
            placeholder="WO-2026-008"
            hint="Its final milestone’s invoice is what releases this to the client."
          />
        ) : null}

        <Field>
          <Small>
            <B>The file</B>
          </Small>
          <Drop
            label="Drop a file, or"
            hint="Up to 2 GB. Executables are refused on the way past."
            onFiles={(files) => {
              setPicked(files[0] ?? null)
              setFailure(null)
            }}
          />
          {picked === null ? null : <Hint>{picked.name}</Hint>}
        </Field>

        {failure === null ? null : (
          <Notice need role="alert">
            {failure}
          </Notice>
        )}

        <Button type="submit" variant="ochre" disabled={upload.isPending || picked === null}>
          {upload.isPending ? 'Uploading…' : 'Upload it'}
        </Button>
      </Stack>
    </form>
  )
}

/* ------------------------------------------------------------------- the call */

type StudioUpload = {
  readonly client: string
  readonly file: File
  readonly values: UploadForm
}

/**
 * The multipart `POST`, and the two invalidations it owes.
 *
 * `files` for the list this dialog sits above, `clients` for the summary head — the
 * same pair `FilesSection` invalidates after a release, and for the same reason:
 * `api/keys.ts` `DOMAIN_ALSO_INVALIDATES` has no `files` row, so nothing joins the
 * two on its own.
 */
function useUploadStudioFile() {
  const api = useApi()
  const queryClient = useQueryClient()

  return useMutation<unknown, ApiError, StudioUpload>({
    mutationFn: async ({ client, file, values }) => {
      const body = new FormData()
      body.set('client', client)
      body.set('kind', values.kind)
      if (values.title !== '') body.set('title', values.title)
      if (values.period !== '') body.set('period', values.period)
      if (values.work_order_ref !== '') body.set('work_order_ref', values.work_order_ref)
      // Last, and named `file`. `modules/files/multipart.ts` requires both.
      body.set('file', file, file.name)

      // No `Content-Type`: the browser writes it with the boundary in it.
      const response = await api.fetch('/api/studio/_upload', {
        method: 'POST',
        credentials: 'include',
        body,
      })
      if (!response.ok) throw await problemOf(response)

      const answer: unknown = await response.json()
      await queryClient.invalidateQueries({ queryKey: moduleKey('files') })
      await queryClient.invalidateQueries({ queryKey: moduleKey('clients') })
      return answer
    },
  })
}
